import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import VslPlayer from "./VslPlayer";
import ParticleHeadline from "./ParticleHeadline";

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=2400&q=85&auto=format&fit=crop";

const H1_LINES = [
  { text: "STOP RENTING", opacity: 0.36, color: "#F0ECE4" },
  { text: "YOUR PIPELINE.", opacity: 1, color: "#F0ECE4" },
  { text: "START OWNING IT.", opacity: 1, color: "#C9A84C" },
];

export default function Hero() {
  const wrap = useRef(null);
  const [loaded, setLoaded] = useState(false);

  const { scrollYProgress } = useScroll({
    target: wrap,
    offset: ["start start", "end start"],
  });
  // Ken Burns + scroll parallax
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const gridY = useTransform(scrollYProgress, [0, 1], [0, 40]);
  const dim = useTransform(scrollYProgress, [0, 1], [1, 0.3]);

  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 120);
    return () => clearTimeout(t);
  }, []);

  const scrollTo = (id) => () => {
    document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="top"
      ref={wrap}
      className="relative min-h-[100svh] w-full overflow-hidden bg-black py-28 md:py-32"
      data-testid="hero-section"
    >
      {/* Image layer with Ken Burns motion */}
      <motion.div
        className="absolute inset-0"
        style={{ y: imgY, scale: imgScale, opacity: dim }}
      >
        <motion.div
          initial={{ scale: 1, x: 0, y: 0 }}
          animate={{ scale: 1.08, x: -30, y: -20 }}
          transition={{ duration: 22, ease: "linear", repeat: Infinity, repeatType: "reverse" }}
          className="h-full w-full"
        >
          <img
            src={HERO_IMAGE}
            alt=""
            className="h-full w-full object-cover"
            style={{ filter: "brightness(0.62) contrast(1.08) saturate(0.85)" }}
          />
        </motion.div>
      </motion.div>

      {/* Overlays */}
      <div
        aria-hidden
        className="absolute inset-0 z-10"
        style={{
          background:
            "linear-gradient(to bottom, rgba(7,7,9,0.35) 0%, rgba(7,7,9,0.72) 60%, rgba(7,7,9,1) 100%)",
        }}
      />
      <motion.div
        aria-hidden
        className="absolute inset-0 z-[11] wam-grid-bg opacity-40 mix-blend-screen"
        style={{ y: gridY }}
      />
      <div
        aria-hidden
        className="absolute inset-0 z-[12] wam-vignette pointer-events-none"
      />

      {/* Content */}
      <motion.div
        className="relative z-20 mx-auto flex min-h-[calc(100svh-14rem)] max-w-[1400px] flex-col items-center justify-center px-6 text-center"
        style={{ y: textY }}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={loaded ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}
          className="wam-eyebrow mb-8"
          data-testid="hero-eyebrow"
        >
          The Webinar Acquisition Model &middot; SBA
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={loaded ? { opacity: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="w-full"
          data-testid="hero-h1"
        >
          <ParticleHeadline
            lines={H1_LINES}
            fontSizeCss="clamp(32px, 7.6vw, 96px)"
          />
        </motion.div>

        {/* Inline VSL player */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={loaded ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1.1, delay: 1.25, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 md:mt-12 w-full"
          data-testid="hero-vsl"
        >
          <div className="mb-3 flex items-center justify-center gap-3 font-mono text-[10px] tracking-[0.3em] text-[#C9A84C]">
            <span className="h-px w-8 bg-[#C9A84C]/50" />
            <span>WATCH THIS FIRST · 3 MIN</span>
            <span className="h-px w-8 bg-[#C9A84C]/50" />
          </div>
          <VslPlayer maxWidth={820} />
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={loaded ? { opacity: 0.6, y: 0 } : {}}
          transition={{ duration: 0.9, delay: 1.5, ease: [0.16, 1, 0.3, 1] }}
          className="mt-12 max-w-[600px] font-body text-[15px] leading-[1.75] text-[#F0ECE4]"
        >
          Most MLOs depend on realtors, Zillow, and shared lead pools they don&rsquo;t
          control. WAM changes that — a direct-to-consumer webinar funnel that
          generates qualified mortgage leads on demand.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={loaded ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 1.55, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 flex flex-wrap items-center justify-center gap-4"
        >
          <button
            onClick={scrollTo("#the-system")}
            className="wam-btn-primary text-[12px]"
            data-testid="hero-cta-primary"
          >
            See How It Works ↓
          </button>
          <button
            onClick={scrollTo("#proof")}
            className="wam-btn-ghost text-[12px]"
            data-testid="hero-cta-secondary"
          >
            Watch The Proof
          </button>
        </motion.div>
      </motion.div>

      {/* Bottom-left proof stat */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={loaded ? { opacity: 0.4 } : {}}
        transition={{ duration: 1.2, delay: 1.9 }}
        className="absolute bottom-8 left-6 md:left-10 z-20 font-mono text-[10px] md:text-[11px] tracking-[0.22em] text-[#F0ECE4]"
        data-testid="hero-proof-stat"
      >
        221 LEADS &middot; 10 DAYS &middot; $800 AD SPEND &middot; CASSANDRA DORSAINVIL
      </motion.div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={loaded ? { opacity: 0.3 } : {}}
        transition={{ duration: 1.2, delay: 2.1 }}
        className="absolute bottom-8 right-6 md:right-10 z-20 flex items-center gap-2"
      >
        <span className="font-mono text-[9px] tracking-[0.3em] text-[#F0ECE4]">
          SCROLL
        </span>
        <motion.svg
          width="10"
          height="20"
          viewBox="0 0 10 20"
          animate={{ y: [0, 6, 0] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
        >
          <path
            d="M5 0 L5 18 M1 14 L5 18 L9 14"
            stroke="#F0ECE4"
            strokeWidth="1"
            fill="none"
          />
        </motion.svg>
      </motion.div>
    </section>
  );
}
